import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  ArrowForward,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/image.jpg';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#0a0a0a',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* ── LEFT PANEL – Brand Hero ───────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 48%',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #0f0f0f 0%, #1a1000 40%, #261500 70%, #0a0a0a 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(255,152,0,0.18) 0%, transparent 70%)',
          },
        }}
      >
        {/* Decorative rings */}
        {[320, 480, 640, 800].map((size, i) => (
          <Box
            key={size}
            sx={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: '50%',
              border: `1px solid rgba(255,152,0,${0.12 - i * 0.025})`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: `pulse${i} ${6 + i * 2}s ease-in-out infinite`,
              [`@keyframes pulse${i}`]: {
                '0%, 100%': { opacity: 0.4, transform: 'translate(-50%, -50%) scale(1)' },
                '50%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.04)' },
              },
            }}
          />
        ))}

        {/* Dot grid pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,152,0,0.15) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            opacity: 0.5,
          }}
        />

        {/* Glow orb */}
        <Box
          sx={{
            position: 'absolute',
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.25) 0%, transparent 70%)',
            top: '30%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            filter: 'blur(40px)',
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            px: 6,
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              position: 'relative',
              mb: 4,
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,152,0,0.3) 0%, transparent 70%)',
                filter: 'blur(12px)',
              },
            }}
          >
            <Avatar
              src={Logo}
              alt="Narnia Farms"
              sx={{
                width: 100,
                height: 100,
                border: '3px solid rgba(255,152,0,0.6)',
                boxShadow: '0 0 40px rgba(255,152,0,0.35), 0 0 80px rgba(255,152,0,0.15)',
              }}
            />
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Narnia{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #ff9800, #ffcc02)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Farms
            </Box>
          </Typography>

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '1rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              mb: 6,
            }}
          >
            Raised with Care · Driven by Quality
          </Typography>

          {/* Feature pills */}
          {[
            { icon: '🐔', label: 'Poultry Management' },
            { icon: '📊', label: 'Real-Time Analytics' },
            { icon: '🌿', label: 'Sustainable Farming' },
          ].map(({ icon, label }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
                px: 2.5,
                py: 1.2,
                borderRadius: '100px',
                background: 'rgba(255,152,0,0.07)',
                border: '1px solid rgba(255,152,0,0.2)',
                width: 'fit-content',
              }}
            >
              <Typography sx={{ fontSize: '1.1rem' }}>{icon}</Typography>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 500 }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Bottom gradient fade */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 160,
            background: 'linear-gradient(to top, #0a0a0a, transparent)',
          }}
        />
      </Box>

      {/* ── RIGHT PANEL – Login Form ──────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 6, lg: 10 },
          py: 6,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1px',
            height: '100%',
            background:
              'linear-gradient(to bottom, transparent, rgba(255,152,0,0.4) 30%, rgba(255,152,0,0.4) 70%, transparent)',
            display: { xs: 'none', md: 'block' },
          },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile logo */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 1.5,
              mb: 5,
            }}
          >
            <Avatar src={Logo} alt="Narnia Farms" sx={{ width: 40, height: 40 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#fff' }}>
              Narnia Farms
            </Typography>
          </Box>

          {/* Heading */}
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', mb: 1 }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', mb: 5, fontSize: '0.95rem' }}>
            Sign in to your farm dashboard
          </Typography>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                background: 'rgba(211,47,47,0.12)',
                border: '1px solid rgba(211,47,47,0.3)',
                color: '#ff8a80',
                '& .MuiAlert-icon': { color: '#ff8a80' },
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: 'rgba(255,152,0,0.6)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.03)',
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.5)' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff9800',
                    boxShadow: '0 0 0 3px rgba(255,152,0,0.12)',
                  },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
                '& input': { color: '#fff' },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #1a1200 inset',
                  WebkitTextFillColor: '#fff',
                },
              }}
            />

            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: 'rgba(255,152,0,0.6)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff9800' } }}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.03)',
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.5)' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff9800',
                    boxShadow: '0 0 0 3px rgba(255,152,0,0.12)',
                  },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
                '& input': { color: '#fff' },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #1a1200 inset',
                  WebkitTextFillColor: '#fff',
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              endIcon={!isLoading && <ArrowForward />}
              sx={{
                mt: 1,
                py: 1.8,
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.01em',
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                color: '#000',
                boxShadow: '0 4px 24px rgba(255,152,0,0.35)',
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ffb300 0%, #ff9800 100%)',
                  boxShadow: '0 6px 32px rgba(255,152,0,0.55)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 12px rgba(255,152,0,0.3)',
                },
                '&:disabled': {
                  background: 'rgba(255,152,0,0.2)',
                  color: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.7)' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>

          {/* Footer */}
          <Typography
            sx={{
              mt: 6,
              textAlign: 'center',
              color: 'rgba(255,255,255,0.2)',
              fontSize: '0.75rem',
              letterSpacing: '0.04em',
            }}
          >
            © {new Date().getFullYear()} Narnia Farms · All rights reserved
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
